import React from 'react';
import { CheckCircle, Info, AlertTriangle, Tag, Zap, ListPlus, Palette } from 'lucide-react';
import { Product, Branch } from '../types';
import WhatsAppProductButton from './WhatsAppProductButton';
import BrandLogo from './BrandLogo';
import ProgressiveImage from './ProgressiveImage';
import { useCartStore } from '../hooks/useCartStore';

interface ProductCardProps {
    product: Product;
    currentBranch: Branch;
    onProductClick?: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, currentBranch, onProductClick }) => {
    // Calculate availability from the stock record
    const isOutOfStock = !product.stock || !Object.values(product.stock).some(Boolean);
    const isAvailable = !isOutOfStock;
    const addItem = useCartStore((state) => state.addItem);

    return (
        <div id={`product-card-${product.id}`} className="group relative bg-zinc-900 border border-zinc-800 hover:border-zinc-400 hover:shadow-[0_0_50px_rgba(255,255,255,0.15)] transition-all duration-500 flex flex-col h-full overflow-hidden select-none">
            {/* Product Image */}
            <div
                id={`product-image-${product.id}`}
                className={`relative aspect-square overflow-hidden border-b border-zinc-800 bg-zinc-900 group ${onProductClick ? 'cursor-pointer' : ''}`}
                onClick={() => onProductClick?.(product)}
            >
                <div className="absolute inset-0 bg-secondary/10 group-hover:bg-transparent transition-colors duration-300" />
                <ProgressiveImage
                    src={product.image}
                    alt={`Case de fibra de carbono real para ${product.name} en El Salvador`}
                    aspectRatio="square"
                    className="transform group-hover:scale-110 transition-transform duration-500 ease-in-out"
                />

                {/* Offer Badge */}
                {product.is_premium && (
                    <div className="absolute top-2 right-2 bg-white text-black text-[10px] font-black uppercase tracking-widest py-1.5 px-3 rounded-sm shadow-xl z-10 flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5" />
                        Exclusivo
                    </div>
                )}

                {/* Out of Stock (Prev. Top Quality) Badge */}
                {isOutOfStock ? (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-[8px] sm:text-[9px] font-black uppercase tracking-widest px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shadow-lg z-20 flex items-center gap-1">
                        <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-white text-white" />
                        <span>AGOTADO</span>
                    </div>
                ) : null}

                {/* Variants Badge */}
                {product.variants && product.variants.length > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-sm border border-zinc-700 text-white text-[8px] sm:text-[9px] font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 sm:py-1 rounded z-20 flex items-center gap-1">
                        <Palette className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                        <span>{product.variants.length} {product.variants.length === 1 ? 'Opción' : 'Opciones'}</span>
                    </div>
                )}

                {/* Hover Quick View Overlay (Desktop) */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="text-white border border-white/30 bg-black/50 backdrop-blur px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transform translate-y-4 group-hover:translate-y-0 transition-transform">
                        Ver Detalles
                    </span>
                </div>
            </div>

            <div className="p-2.5 sm:p-4 flex flex-col flex-grow relative z-10">
                {/* Product Info */}
                <div
                    className={`mb-2 sm:mb-3 ${onProductClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onProductClick?.(product)}
                >
                    <div className="flex flex-col gap-1.5 mb-2 sm:mb-3">
                        <div className="flex items-center">
                            <span className="text-[9px] sm:text-[10px] font-bold text-white uppercase tracking-wider px-2 py-0.5 rounded-sm bg-white/5 border border-white/10 shadow-sm">
                                {product.category}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <BrandLogo brandName={product.make} className="w-3 h-3 md:w-3.5 md:h-3.5" noFilter />
                            <span className="text-[9px] sm:text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                                {product.make}
                            </span>
                        </div>
                    </div>
                    <h3 className="text-[13px] sm:text-lg font-display font-bold text-white leading-tight uppercase italic group-hover:text-white transition-colors line-clamp-2 min-h-[2rem] sm:min-h-[3.5rem]">
                        {product.name}
                    </h3>
                </div>

                <div className="mt-auto">
                    {/* Stock Status */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-4 border-b border-zinc-800 pb-2 sm:pb-3 gap-1">
                        <div className={`flex items-center gap-1.5 text-[10px] sm:text-xs uppercase font-bold tracking-wide ${isAvailable ? 'text-white' : 'text-orange-500'}`}>
                            {isAvailable ? <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" /> : <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                            {isAvailable ? 'En Stock' : 'Por pedido'}
                        </div>

                        {/* Urgency Badge */}
                        <div className="flex flex-col sm:items-end">
                            {product.is_last_units && isAvailable && (
                                <div className="text-[8px] sm:text-[9px] font-bold text-red-500 uppercase tracking-wider animate-pulse">
                                    ¡Últimas unidades!
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Availability Details */}
                    {!isAvailable && (
                        <div className="hidden sm:flex text-[10px] text-zinc-500 mb-3 items-start gap-1 leading-tight">
                            <AlertTriangle className="h-3 w-3 flex-shrink-0 text-orange-500" />
                            <span>Sin stock en {currentBranch.name}.</span>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <WhatsAppProductButton
                            product={product}
                            currentBranch={currentBranch}
                            isAvailable={isAvailable}
                            className="flex-grow bg-white hover:bg-zinc-200 text-black border border-transparent transition-all duration-300 py-2 sm:py-3 flex items-center justify-center gap-1.5 sm:gap-2 font-display font-bold uppercase tracking-wider text-[9px] sm:text-[11px] group-btn shadow-lg min-w-0"
                            variant="default"
                            mobileText="COMPRAR"
                        />
                        <button
                            id={`btn-add-list-${product.id}`}
                            onClick={() => addItem(product)}
                            className="px-3 sm:px-4 bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white hover:border-white transition-all duration-300 rounded-none group/cart"
                            title="Añadir a lista de consulta"
                        >
                            <ListPlus className="h-4 w-4 sm:h-5 sm:w-5 group-hover/cart:scale-110 transition-transform" />
                        </button>
                    </div>
                    <span className="hidden sm:flex text-[10px] text-zinc-500 text-center font-light items-center justify-center gap-1 mt-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        {isAvailable ? 'Respuesta en minutos' : 'Te ayudamos a encontrarlo'}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
